<template>
  <lb-dialog
    v-model="internalValue"
    :loading="loading"
    :heading="heading"
    :width="computedWidth"
    data-testid="ru-approval-dialog"
  >
    <template v-slot:body>
      <div data-testid="ru-approval-dialog__body" class="d-flex flex-column gap-4">
        <v-chip class="noTransform ru-info-chip gap-2 align-center">
          <span class="d-flex align-center gap-2 noTransform"><v-icon>mdi-information-outline</v-icon> 
            The RU level approval comment in the PDF report would be restricted to 500 characters.</span>
        </v-chip>
        
        <!-- Table showing eligible RUs for approval -->
        <div 
          v-if="hasEligibleReportingUnits" 
          data-testid="ru-approval-dialog__table-wrapper"
        >
          <lb-datatablenew
            :headers="headers"
            :items="formattedEligibleReportingUnits"
            :enableslot="['l1Status', 'l2Status', 'reportingUnit']"
            :loading="loading"
            :perpage="10"
            data-testid="ru-approval-dialog__table"
          >
            <template v-slot:l1Status="{ item }">
              <StatusIndicator
                :variant="item.l1StatusVariant || getStatusVariant(item.l1Status, 'local')"
                :custom-label="item.l1StatusLabel || '-'"
                size="sm"
              />
            </template>
            <template v-slot:l2Status="{ item }">
              <StatusIndicator
                :variant="item.l2StatusVariant || getStatusVariant(item.l2Status, 'regional')"
                :custom-label="item.l2StatusLabel || '-'"
                size="sm"
              />
            </template>
            <template v-slot:reportingUnit="{ item }">
              <span class="font-weight-medium">{{ item.code }}</span> - {{ item.name }}
            </template>
          </lb-datatablenew>
        </div>
        
        <h5 class="font-weight-medium">Approval Comment</h5>
        <div v-if="approvalType === 'local' || approvalType === 'regional'">
          <lb-textarea
            :rules="[v => (v || '').length <= 500 || '']"
            :hidedetails="false"
            :value="internalNote"
            @input="updateNote"
            data-testid="ru-approval-dialog__textarea"
            class="approve-comment-textarea"
          ></lb-textarea>
        </div>
      </div>
    </template>
    <template v-slot:actions>
      <v-spacer />
      <v-btn 
        class="ml-2" 
        color="primary" 
        @click="emitProceed"
        :disabled="!hasEligibleReportingUnits && eligibleReportingUnits !== null"
        data-testid="ru-approval-dialog__proceed-btn"
      >
        <span>Proceed</span>
      </v-btn>
    </template>
  </lb-dialog>
</template>

<script>
import StatusIndicator from '../components/StatusIndicator.vue';
import { STATUS_VARIANTS } from '../utils/statusConstants';
import { getStatusVariantByCode } from '../utils/statusHelpers';

/**
 * RUApprovalDialog Component
 * 
 * A dialog for RU (Reporting Unit) level approval with a table showing
 * eligible reporting units and their status information.
 * 
 * @component
 * @example
 * <RUApprovalDialog
 *   v-model="showDialog"
 *   :approval-type="'local'"
 *   :eligible-reporting-units="eligibleRUs"
 *   :note="approvalNote"
 *   @update:note="handleNoteUpdate"
 *   @proceed="handleApproval"
 * />
 */

export default {
  name: 'RUApprovalDialog',
  
  components: {
    StatusIndicator
  },
  
  props: {
    /**
     * Controls dialog visibility (v-model)
     * @type {Boolean}
     * @default false
     */
    value: { type: Boolean, default: false },
    /**
     * Loading state for the dialog
     * @type {Boolean}
     * @default false
     */
    loading: { type: Boolean, default: false },
    /**
     * Dialog heading
     * @type {String|Function}
     * @default ''
     */
    heading: { type: [String, Function], default: '' },
    /**
     * Dialog width (used as base, computed width may be larger if table shown)
     * @type {String|Number}
     * @default 800
     */
    width: { type: [String, Number], default: 800 },
    /**
     * Type of approval - 'local' or 'regional'
     * @type {String}
     * @default 'local'
     */
    approvalType: { type: String, default: 'local' },
    /**
     * Approval note/comment
     * @type {String}
     * @default ''
     */
    note: { type: String, default: '' },
    /**
     * List of eligible reporting units to display in the table
     * Each item should have: cycleId, code, name, l1Status, l1StatusLabel, l2Status, l2StatusLabel, userRole
     * @type {Array}
     * @default []
     */
    eligibleReportingUnits: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      headers: [
        { text: 'S.No', value: 'serial', sortable: false, width: '60px', align: 'center' },
        { 
          text: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'Local')} ${this.$nova.labelsName('my_reporting_units_page', 'commenrary_ru_label', 'RU')} Status`, 
          value: 'l1Status', 
          sortable: false, 
          align: 'left',
          width: '180px'
        },
        { 
          text: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'Regional')} ${this.$nova.labelsName('my_reporting_units_page', 'commenrary_ru_label', 'RU')} Status`, 
          value: 'l2Status', 
          sortable: false, 
          align: 'left',
          width: '200px' 
        },
        { text: 'User Role', value: 'userRole', sortable: false, align: 'left', width: '180px' },
        { text: 'Reporting Unit', value: 'reportingUnit', sortable: false, align: 'left' }
      ]
    };
  },

  computed: {
    formattedEligibleReportingUnits() {
      return (this.eligibleReportingUnits || []).map((ru, index) => ({
        ...ru,
        serial: index + 1,
        reportingUnit: `${ru.code} - ${ru.name}`
      }));
    },
    /**
     * Internal value for v-model support
     */
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    /**
     * Label for the textarea
     * @returns {String}
     */
    textareaLabel() {
      return 'Approval Comment*';
    },
    /**
     * Internal note value
     * @returns {String}
     */
    internalNote() {
      return this.note;
    },
    /**
     * Check if there are eligible reporting units to display
     * @returns {Boolean}
     */
    hasEligibleReportingUnits() {
      return this.eligibleReportingUnits && this.eligibleReportingUnits.length > 0;
    },
    /**
     * Compute dialog width based on whether table is shown
     * @returns {String|Number}
     */
    computedWidth() {
      return this.hasEligibleReportingUnits ? 900 : this.width;
    }
  },
  
  methods: {
    /**
     * Emit proceed event for approval
     * @emits proceed
     */
    emitProceed() {
      this.$emit('proceed');
    },
    /**
     * Update note value
     * @param {String} value - New note value
     * @emits update:note
     */
    updateNote(value) {
      this.$emit('update:note', value);
    },
    /**
     * Get the status variant based on status code
     * @param {String|Number} status - The status code
     * @param {String} type - The approval type ('local' or 'regional')
     * @returns {String} Status variant
     */
    getStatusVariant(status, type = 'local') {
      // Override for regional mapping to ensure correct labels/colors
      // Matches logic in List.vue resolveApprovalStatus
      if (type === 'regional') {
        const code = Number(status);
        if (code === 0 || code === 1) return STATUS_VARIANTS.REGIONAL_PENDING;
        if (code === 2) return STATUS_VARIANTS.APPROVED;
        if (code === 3) return STATUS_VARIANTS.DISABLED;
      }
      
      // Use the centralized helper for local or fallback
      return getStatusVariantByCode(status) || STATUS_VARIANTS.UNKNOWN;
    }
  },
};
</script>

<style scoped>
/* Status chip styling */
.status-chip {
  font-size: 11px !important;
  height: 20px !important;
}

::v-deep .approve-comment-textarea textarea {
  color: #828285;
  font-size: 12px;
  padding: 8px 12px;
}
::v-deep .approve-comment-textarea .v-input__slot{
  border-radius: 8px;
}
</style>
